/* eslint-disable radix */
import React, { useEffect, useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { parse, ParseAsyncConfig, Parser, ParseStepResult } from 'papaparse';
import styled from 'styled-components';
import validator from 'validator';
import { FileUploadButton, FileUploadDropZone, H3, HeaderCell, HeaderRow, LayoutSidebar, LoaderModal, Message, ModalFactory, Table, TableBody, TableHead, UploadPaymentSummarySidebar } from 'components';
import { ISidebarNotification } from 'components/Sidebar/components/Sidebar';
import UploadInstructionsSideBar from 'components/Sidebar/UploadInstructionsSidebar';
import { appConfig } from 'config/app';
import { MakePaymentNotification } from 'config/const';
import { theme, toRem } from 'design';
import { Align, Appearance } from 'design/types';
import { UploadPaymentMatch } from 'modals';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Payment, uploadPaymentRequest } from 'store/Payment';
import { IStore } from 'store/types';
import { IEmployee, IPayee, ISiteRolePolicy, IUploadPayment } from 'types';
import { createPayeeFromEmployee, isEmployeePayable, stripSpaces, sumBy } from 'utils';
import { MakePaymentHeader } from '../../components';
import UploadPaymentSelector from '../../components/UploadPaymentSelector/UploadPaymentSelector';
import { MPContent } from '../../styles';
import { MakePaymentProps } from '../../types';

interface IUploadPaymentProps extends MakePaymentProps {
  paymentUploads: IUploadPayment[];
  setPaymentUploads: (payments: IUploadPayment[]) => void;
  setPayees: (payees: IPayee[], replace?: boolean) => void;
  onRemoveAllPayees: () => void;
  rolePolicy?: ISiteRolePolicy;
}

const UploadContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-bottom: ${toRem(theme.space.lg)};
`;

const ImportContainer = styled.div`
  width: 100%;
  flex-flow: column nowrap;
`;

const StyledMessage = styled(Message)`
  margin-bottom: ${toRem(theme.space.lg)};
`;

const PAYMENT_LIMIT = 150;

const validateAmount = (field: string, amount: string) => {
  if (!amount) return;
  if (!validator.isCurrency(amount.trim(), { allow_negatives: false, digits_after_decimal: [1, 2] })) {
    throw new Error(`An invalid ${field} value was found`);
  }
};

const validateString = (field: string, value: string, maxLength: number, required = true) => {
  if (required && !value) throw new Error(`Missing ${field} value`);
  if (value.length > maxLength) throw new Error(`${field} cannot be more than ${maxLength} characters`);
};

const UploadPayment = ({ pageHeaderContent, currentEmployee, currentSiteId, onBack, onNext, paymentUploads, setPaymentUploads, setPayees, onRemoveAllPayees, rolePolicy }: IUploadPaymentProps) => {
  const dispatch = useDispatch();
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);
  const [isBuildingPayeeList, setIsBuildingPayeeList] = useState<boolean>(false);
  const [selectedPayment, setSelectedPayment] = useState<{ index: number; payment: IUploadPayment } | undefined>(undefined);
  const isImportingData = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payment.UPLOAD_PAYMENT_REQUEST));

  useEffect(() => {
    if (isBuildingPayeeList) {
      if (paymentUploads.length > 0) {
        const newPayees = paymentUploads.reduce((acc: IPayee[], payment) => {
          if (payment.employee) acc.push(createPayeeFromEmployee(payment.employee, currentSiteId, payment.amount, payment.cash, payment.notes));
          return acc;
        }, []);
        setPayees(newPayees, true);
        onNext();
      }
      setIsBuildingPayeeList(false);
    }
  }, [paymentUploads, isBuildingPayeeList]);

  const parsedPayments: IUploadPayment[] = [];

  const handleCsvUpload = (file: any) => {
    const includedHeaders = { amount: false, cash: false, name: false, employeeId: false, firstAndLastName: false, notes: false };
    let rowNum = 1; // Start indexing at 1 to accommodate header row

    const parseConfig: ParseAsyncConfig = {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (header: string) => header.trim().toLowerCase().split(' ').join(''), // trim whitespace and concatenate headers
      step: (results: ParseStepResult<any>, parser: Parser) => {
        const { data, meta } = results;
        const fields = meta.fields || [];

        const abort = (errorMessage: string, row?: number) => {
          setUploadError(`${errorMessage}${row ? ` (Error on Line ${row})` : ''}. Please review the formatting and try again.`);
          parser.abort();
        };

        // Abort upload if payment limit reached
        if (rowNum > PAYMENT_LIMIT) {
          abort(`File cannot contain more than ${PAYMENT_LIMIT} payments`);
          return;
        }

        if (rowNum === 1) {
          includedHeaders.amount = fields.includes('amount');
          includedHeaders.cash = fields.includes('cash');
          includedHeaders.notes = fields.includes('notes');
          includedHeaders.employeeId = fields.includes('employeeid');
          includedHeaders.name = fields.includes('name');
          if (!includedHeaders.name) includedHeaders.firstAndLastName = fields.includes('firstname') && fields.includes('lastname');

          // Required CSV Headers: `amount` - 'name' or 'firstname' and 'lastname' or 'employeeid'
          if (!includedHeaders.amount || (!includedHeaders.name && !includedHeaders.firstAndLastName && !includedHeaders.employeeId)) {
            abort('The header values are invalid');
            return;
          }
        }

        rowNum++;

        const { amount, cash, name, firstname, lastname, employeeid, notes } = data;
        const payment: IUploadPayment = { amount: 0 };

        // Names pasted from other tools can carry non-breaking spaces, which the name match treats as distinct characters
        const payeeName = stripSpaces(name);
        const payeeFirstName = stripSpaces(firstname);
        const payeeLastName = stripSpaces(lastname);
        const payeeEmployeeId = employeeid?.trim();
        const payeeNote = notes?.trim();

        try {
          if (includedHeaders.firstAndLastName) {
            validateString('First Name', payeeFirstName, appConfig.NAME.MAX);
            validateString('Last Name', payeeLastName, appConfig.NAME.MAX);
            payment.name = `${payeeFirstName} ${payeeLastName}`;
          }
          if (includedHeaders.name) {
            validateString('Name', payeeName, appConfig.NAME.MAX * 2);
            payment.name = payeeName;
          }
          if (includedHeaders.employeeId) {
            validateString('Employee ID', payeeEmployeeId, appConfig.EMPLOYEE_ID.MAX);
            payment.employeeId = payeeEmployeeId;
          }
          if (includedHeaders.notes) {
            validateString('Notes', payeeNote, appConfig.PAYEE_NOTE.MAX, false);
            payment.notes = payeeNote;
          }
          if (includedHeaders.cash) {
            validateAmount('Cash', cash);
            payment.cash = formatAmount(cash);
          }
          validateAmount('Amount', amount);
          payment.amount = formatAmount(amount);
        } catch (error: any) {
          abort(error.message, rowNum);
          return;
        }

        parsedPayments.push(payment);
      },
      complete: (results: ParseStepResult<any>) => {
        // Handle empty file
        if (!results.meta.aborted && parsedPayments.length === 0) {
          setUploadError('File is empty.');
          return;
        }

        // Process upload payments (Match Employees by name)
        if (!results.meta.aborted && parsedPayments.length > 0) {
          dispatch(uploadPaymentRequest(currentSiteId, parsedPayments, setPaymentUploads));
        }
      },
      error: (err: Error) => {
        setUploadError(err.message);
      },
    };

    if (!file) return;

    // clear the uploaded payment state
    clearUpload();

    parse(file, parseConfig);
  };

  const formatAmount = (tipAmount: any) => {
    if (!tipAmount) return 0;

    const tipAmountAsString = tipAmount.toString().replaceAll('$', '');
    // if there are no decimals, return the amount in cents
    // Ex. 100 becomes 10000 which will evaluate as $100.00 in the payment form
    if (tipAmountAsString.split('.').length === 1) return parseInt(tipAmountAsString, 0) * 100;

    // Decimals ending in 0 are ignored during the file upload (100.50 imports as 100.5) so we need to check if there
    // is only one decimal place and append the 0 back on so the value correctly evaluates as cents
    // Ex. 100.5 becomes 10050 which will evaluate as $100.50 in the payment form
    if (tipAmountAsString.split('.')[1].length === 1) return parseInt(tipAmountAsString.split('.').join(''), 0) * 10;

    // if there are decimal places, remove the decimal and return the whole number
    // Ex. 100.98 becomes 10090 which will evaluate as $100.98 in the payment form
    return parseInt(tipAmountAsString.split('.').join(''), 0);
  };

  const clearUpload = () => {
    setUploadError(undefined);
    setPaymentUploads([]);
    onRemoveAllPayees();
  };

  const removePaymentUpload = (index: number) => {
    if (paymentUploads.length > 0) {
      onRemoveAllPayees();
      setPaymentUploads(paymentUploads.filter((_, i) => i !== index));
    }
  };

  const setPaymentEmployee = (index: number, payment: IUploadPayment) => {
    setSelectedPayment({ index, payment });
    showPaymentModal();
  };

  const onUpdatePayment = (index: number, newPayment: IUploadPayment) => {
    if (paymentUploads.length > 0) {
      onRemoveAllPayees();
      setPaymentUploads(paymentUploads.map((p, i) => (i === index ? newPayment : p)));
      hidePaymentModal();
    }
  };

  const getEmployeeErrors = useMemo(() => {
    const notifications: ISidebarNotification[] = [];
    if (rolePolicy && paymentUploads?.some((upload) => upload.employee && !isEmployeePayable(upload.employee, rolePolicy, currentEmployee))) {
      notifications.push(MakePaymentNotification.upload.ineligibleMatch);
    } else if (paymentUploads?.some((upload) => !upload.employee && !upload.employeeMatch.length)) {
      notifications.push(MakePaymentNotification.upload.matchNotFound);
    } else if (paymentUploads?.some((upload) => !upload.employee && upload.employeeMatch.length > 1)) {
      notifications.push(MakePaymentNotification.upload.multipleMatchesFound);
    }

    return notifications;
  }, [paymentUploads]);

  const isEmployeeDisabled = (employee: IEmployee) => {
    if (paymentUploads.some((payment) => payment.employee?.id === employee.id)) {
      return 'Employee has already been added';
    }
    if (rolePolicy && !isEmployeePayable(employee, rolePolicy, currentEmployee)) {
      return 'You are not allowed to issue tips to this user.';
    }
    return '';
  };

  const [showPaymentModal, hidePaymentModal] = useModal(
    () => (
      <ModalFactory customModal onClose={hidePaymentModal} isActive closeOnOutSideClick={false}>
        {selectedPayment && selectedPayment.payment && (
          <UploadPaymentMatch
            payment={selectedPayment.payment}
            onClose={hidePaymentModal}
            siteId={currentSiteId}
            onUpdatePayment={(payment: IUploadPayment) => onUpdatePayment(selectedPayment.index, payment)}
            isEmployeeDisabled={isEmployeeDisabled}
          />
        )}
      </ModalFactory>
    ),
    [selectedPayment, currentSiteId, isEmployeeDisabled]
  );

  const paymentsAreEmpty = isEmpty(paymentUploads);
  const disableNext = paymentsAreEmpty || !rolePolicy || paymentUploads.some((pu) => pu.employee === undefined || !isEmployeePayable(pu.employee, rolePolicy, currentEmployee));
  const totalTips = sumBy(paymentUploads || [], 'amount');
  const totalCash = sumBy(paymentUploads || [], 'cash');
  const hasEmployeeId = !paymentsAreEmpty && 'employeeId' in paymentUploads[0];
  const hasCash = !paymentsAreEmpty && 'cash' in paymentUploads[0];

  return (
    <LayoutSidebar
      content={
        <>
          <MakePaymentHeader data-cy="make-payment-header" pageHeaderContent={pageHeaderContent} />
          <MPContent>
            <LoaderModal isActive={paymentsAreEmpty && isImportingData} title="Loading Data" message="One moment please..." />
            {uploadError && <StyledMessage dataCy="upload-payment-error" appearance={Appearance.DANGER} title="There was an error uploading your file." message={uploadError} alignMessage={Align.CENTER} />}
            {!isImportingData && paymentsAreEmpty && <FileUploadDropZone onUpload={handleCsvUpload} accept=".csv" />}
            {!uploadError && !paymentsAreEmpty && (
              <ImportContainer>
                <UploadContainer>
                  <H3>Payment Upload</H3>
                  <FileUploadButton label="Upload New File" size="small" appearance="primary" accept=".csv" onUpload={handleCsvUpload} />
                </UploadContainer>
                <Table scrollable data-cy="upload-payment-table">
                  <TableHead>
                    <HeaderRow sticky>
                      <HeaderCell>{hasEmployeeId ? 'Employee' : 'Name'}</HeaderCell>
                      {hasCash && <HeaderCell>Cash</HeaderCell>}
                      <HeaderCell>Amount</HeaderCell>
                      <HeaderCell />
                    </HeaderRow>
                  </TableHead>
                  <TableBody>
                    {paymentUploads.map((payment: IUploadPayment, idx) => (
                      <UploadPaymentSelector
                        key={`upload-payment-${idx}`}
                        payment={payment}
                        onRemove={() => removePaymentUpload(idx)}
                        onSetEmployee={(pmt) => setPaymentEmployee(idx, pmt)}
                        disabled={payment.employee && rolePolicy && !isEmployeePayable(payment.employee, rolePolicy, currentEmployee)}
                        tooltipId={`payee-upload-${idx}`}
                      />
                    ))}
                  </TableBody>
                </Table>
              </ImportContainer>
            )}
          </MPContent>
        </>
      }
      sidebar={
        paymentsAreEmpty || uploadError ? (
          <UploadInstructionsSideBar onBack={onBack} />
        ) : (
          <UploadPaymentSummarySidebar totals={{ gross: totalTips, cash: totalCash }} hasCash={hasCash} notifications={getEmployeeErrors} onBack={onBack} onContinue={() => setIsBuildingPayeeList(true)} continueDisabled={disableNext} />
        )
      }
    />
  );
};

export default UploadPayment;
